"use client";

import React, { useMemo } from 'react';
import { Editor } from '@tiptap/react';
import { CheckCircle2, Loader2 } from 'lucide-react';

type MapleWordStatusBarProps = {
  editor: Editor | null;
  wordsFallback: number;
  charsFallback: number;
  isSaving: boolean;
  autoSaving: boolean;
  isSaved: boolean;
};

const MapleWordStatusBar: React.FC<MapleWordStatusBarProps> = ({
  editor,
  wordsFallback,
  charsFallback,
  isSaving,
  autoSaving,
  isSaved,
}) => {
  const words = useMemo(
    () => (editor?.storage?.characterCount?.words?.() ?? wordsFallback),
    [editor, wordsFallback]
  );
  const chars = useMemo(
    () => (editor?.storage?.characterCount?.characters?.() ?? charsFallback),
    [editor, charsFallback]
  );

  const saving = isSaving || autoSaving;

  return (
    <div className="border-t border-border bg-muted/40 backdrop-blur-sm px-3 py-2 text-xs text-muted-foreground flex items-center justify-between">
      <div className="flex items-center gap-3">
        <span className="font-medium text-foreground">
          {words} words
        </span>
        <span className="text-muted-foreground">•</span>
        <span className="font-medium text-foreground">
          {chars} chars
        </span>
      </div>

      <div className="flex items-center gap-2">
        {saving ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            <span className="text-blue-600">Saving…</span>
          </>
        ) : isSaved ? (
          <>
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="text-green-600">Saved</span>
          </>
        ) : (
          <span className="text-muted-foreground">Editing</span>
        )}
      </div>
    </div>
  );
};

export default MapleWordStatusBar;